/**
 * Daily check card for the converted-lead lifecycle timeline.
 *
 * Three render variants in this single component:
 *  - "completed": Claude actually ran for this day; show outcome + tokens +
 *    expand-toggles for transcript and raw response.
 *  - "skipped": rendered after a confirmation; lifecycle ended early.
 *  - "pending": placeholder for an upcoming day in an in-progress lifecycle.
 *
 * Caller picks the variant from the lifecycle/timeline counts.
 */

import { Card, CardContent } from '@/components/ui/card'
import { Check, Circle, CircleDashed, Mic } from 'lucide-react'
import { ConversationTranscript } from './ConversationTranscript'
import { formatCurrency } from '@/lib/ghl-qualifier'
import type { QualifierAnalysisRow } from '@/hooks/useGHLQualifierDashboard'

/**
 * Format a Whisper cost USD value with up to 4 decimals — short transcription
 * costs hit the sub-cent range so we need more precision than the standard
 * currency formatter offers.
 */
function formatWhisperCost(usd: number | null): string {
  if (usd == null) return '—'
  if (usd === 0) return '$0.00'
  if (usd < 0.01) return `$${usd.toFixed(4)}`
  return `$${usd.toFixed(2)}`
}

function formatWhisperDuration(seconds: number | null): string {
  if (seconds == null || seconds === 0) return '—'
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

interface CompletedProps {
  readonly variant: 'completed'
  readonly day: number
  readonly analysis: QualifierAnalysisRow
  readonly didConfirm: boolean
  readonly confirmationActions?: readonly string[]
}

interface SkippedProps {
  readonly variant: 'skipped'
  readonly day: number
  readonly reason?: string
}

interface PendingProps {
  readonly variant: 'pending'
  readonly day: number
  readonly estimatedAt?: Date | null
}

type Props = CompletedProps | SkippedProps | PendingProps

function CompletedCard({
  day,
  analysis,
  didConfirm,
  confirmationActions,
}: Omit<CompletedProps, 'variant'>) {
  const tokensIn = analysis.input_tokens ?? 0
  const tokensOut = analysis.output_tokens ?? 0
  const priceQuoted =
    typeof analysis.extracted_fields === 'object' && analysis.extracted_fields !== null
      ? ((analysis.extracted_fields as Record<string, unknown>).price_quoted ?? null)
      : null
  const priceConfirmed =
    typeof analysis.extracted_fields === 'object' && analysis.extracted_fields !== null
      ? ((analysis.extracted_fields as Record<string, unknown>).price_confirmed ?? null)
      : null
  const confirmationPhrase =
    typeof analysis.extracted_fields === 'object' && analysis.extracted_fields !== null
      ? ((analysis.extracted_fields as Record<string, unknown>).confirmation_phrase ?? null)
      : null

  return (
    <Card className={didConfirm ? 'border-green-300 bg-green-50/40' : ''}>
      <CardContent className="pt-5 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">Day {day}</span>
          <span className="text-xs text-muted-foreground">
            <time dateTime={analysis.created_at}>
              {new Date(analysis.created_at).toLocaleString()}
            </time>
          </span>
          <span className="ml-auto flex items-center gap-1.5 text-xs">
            {didConfirm ? (
              <>
                <Check className="h-4 w-4 text-green-600" />
                <span className="font-medium text-green-700">CONFIRMED</span>
              </>
            ) : (
              <>
                <Circle className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Not confirmed</span>
              </>
            )}
          </span>
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {tokensIn.toLocaleString()}/{tokensOut.toLocaleString()} tokens
          </span>
        </div>

        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-0.5 text-sm">
          <dt className="text-muted-foreground">Confirmed</dt>
          <dd>{didConfirm ? 'true' : 'false'}</dd>
          {priceQuoted != null ? (
            <>
              <dt className="text-muted-foreground">Price quoted</dt>
              <dd>{formatCurrency(typeof priceQuoted === 'number' ? priceQuoted : null)}</dd>
            </>
          ) : null}
          {priceConfirmed != null ? (
            <>
              <dt className="text-muted-foreground">Price confirmed</dt>
              <dd>{priceConfirmed ? 'true' : 'false'}</dd>
            </>
          ) : null}
          <dt className="text-muted-foreground">Messages analyzed</dt>
          <dd className="tabular-nums">{analysis.message_count}</dd>
          {analysis.whisper_calls != null && analysis.whisper_calls > 0 ? (
            <>
              <dt className="text-muted-foreground flex items-center gap-1.5">
                <Mic className="h-3.5 w-3.5" />
                Whisper
              </dt>
              <dd className="tabular-nums">
                {analysis.whisper_calls}{' '}
                {analysis.whisper_calls === 1 ? 'call' : 'calls'}
                <span className="text-muted-foreground"> · </span>
                {formatWhisperDuration(analysis.whisper_duration_seconds)}
                <span className="text-muted-foreground"> · </span>
                <span className="font-medium">
                  {formatWhisperCost(analysis.whisper_cost_usd)}
                </span>
              </dd>
            </>
          ) : null}
        </dl>

        {analysis.reasoning ? (
          <div className="text-sm">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              Reasoning:
            </span>{' '}
            <span className="whitespace-pre-wrap">{analysis.reasoning}</span>
          </div>
        ) : null}

        {didConfirm && typeof confirmationPhrase === 'string' && confirmationPhrase ? (
          <p className="text-sm">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              Confirmation phrase:
            </span>{' '}
            <em>"{confirmationPhrase}"</em>
          </p>
        ) : null}

        <div className="flex flex-wrap gap-3">
          <details className="text-sm">
            <summary className="cursor-pointer text-xs uppercase tracking-wide text-muted-foreground">
              Expand transcript
            </summary>
            <div className="mt-2">
              <ConversationTranscript rawMessages={analysis.raw_messages} />
            </div>
          </details>
          {analysis.raw_claude_response ? (
            <details className="text-sm">
              <summary className="cursor-pointer text-xs uppercase tracking-wide text-muted-foreground">
                View raw response
              </summary>
              <pre className="mt-2 overflow-x-auto rounded-md bg-muted/50 p-3 text-[11px]">
                {analysis.raw_claude_response}
              </pre>
            </details>
          ) : null}
        </div>

        {didConfirm && confirmationActions && confirmationActions.length > 0 ? (
          <ul className="border-t pt-3 space-y-1 text-sm">
            {confirmationActions.map((action) => (
              <li key={action} className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600" />
                <span>{action}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  )
}

function SkippedCard({ day, reason }: Omit<SkippedProps, 'variant'>) {
  return (
    <Card className="bg-muted/30">
      <CardContent className="py-3 flex items-center gap-3 text-sm text-muted-foreground">
        <span className="font-semibold text-foreground">Day {day}</span>
        <span>—</span>
        <span>{reason ?? 'Skipped'}</span>
      </CardContent>
    </Card>
  )
}

function PendingCard({ day, estimatedAt }: Omit<PendingProps, 'variant'>) {
  return (
    <Card className="border-dashed bg-muted/10">
      <CardContent className="py-3 flex items-center gap-3 text-sm">
        <CircleDashed className="h-4 w-4 text-muted-foreground" />
        <span className="font-semibold">Day {day}</span>
        <span className="text-muted-foreground">Pending</span>
        {estimatedAt ? (
          <span className="ml-auto text-xs text-muted-foreground">
            Est. <time dateTime={estimatedAt.toISOString()}>{estimatedAt.toLocaleString()}</time>
          </span>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function DailyCheckCard(props: Props) {
  if (props.variant === 'completed') {
    return (
      <CompletedCard
        day={props.day}
        analysis={props.analysis}
        didConfirm={props.didConfirm}
        confirmationActions={props.confirmationActions}
      />
    )
  }
  if (props.variant === 'skipped') {
    return <SkippedCard day={props.day} reason={props.reason} />
  }
  return <PendingCard day={props.day} estimatedAt={props.estimatedAt} />
}
